import styles from './LeakDiagram.module.css'

/**
 * The DNS-leak explainer, as inline SVG.
 *
 * Replaces the old raster (dnsleakimg2.png: ~76KB, baked-in English, one theme,
 * a network fetch). Your device asks the resolver you chose and gets an answer
 * (top exchange); the same lookup also goes to a resolver you did not choose,
 * which sees it and answers too (the leak, below). Both exchanges are two-way,
 * matching the original's four steps.
 *
 * All flows are horizontal, so their labels sit cleanly above or below the line
 * with nothing overlapping. The device is one tall node whose upper edge talks
 * to the top resolver and lower edge to the bottom one.
 *
 * Nothing is fetched, so it renders in mainland China; it inherits the theme
 * tokens; and under prefers-reduced-motion it shows the finished diagram at
 * rest. Both destinations are labelled in text, so it reads without colour.
 */
export default function LeakDiagram() {
    return (
        <figure className={styles.figure}>
            <svg
                className={styles.svg}
                viewBox="0 0 640 300"
                role="img"
                aria-label="Your device asks the resolver you chose and gets an answer. The same lookup also goes to a resolver you did not choose, which sees it and answers too — that is the leak."
                preserveAspectRatio="xMidYMid meet"
            >
                {/* the boundary between your network and the wider internet */}
                <line className={styles.divide} x1="300" y1="24" x2="300" y2="276" />
                <text className={styles.zone} x="150" y="38" textAnchor="middle">your device</text>
                <text className={styles.zone} x="470" y="38" textAnchor="middle">the internet</text>

                {/* your device: one tall node feeding both exchanges */}
                <g className={styles.node}>
                    <rect x="60" y="90" width="120" height="150" rx="12" />
                    <rect className={styles.screen} x="80" y="132" width="52" height="36" rx="4" />
                    <rect className={styles.screen} x="142" y="146" width="22" height="30" rx="4" />
                </g>

                {/* the resolver you chose (top) */}
                <g className={`${styles.node} ${styles.ok}`}>
                    <rect x="452" y="86" width="124" height="52" rx="10" />
                    <circle cx="468" cy="112" r="4" />
                    <text className={styles.serverLabel} x="514" y="76" textAnchor="middle">resolver you chose</text>
                </g>

                {/* the resolver you did not (bottom) */}
                <g className={`${styles.node} ${styles.leak}`}>
                    <rect x="452" y="196" width="124" height="52" rx="10" />
                    <circle cx="468" cy="222" r="4" />
                    <text className={styles.serverLabel} x="514" y="266" textAnchor="middle">resolver you did not</text>
                </g>

                {/* top exchange: query out, answer back */}
                <g className={styles.flowOk}>
                    <path className={styles.query} d="M180 106 H448" markerEnd="url(#arrowOk)" />
                    <text className={styles.step} x="314" y="98" textAnchor="middle">which IP is example.com?</text>
                    <path className={styles.answer} d="M448 126 H180" markerEnd="url(#arrowOkBack)" />
                    <text className={styles.stepMuted} x="314" y="142" textAnchor="middle">93.184.216.34</text>
                </g>

                {/* the leak: the same lookup, seen and answered by a resolver you
                    never chose */}
                <g className={styles.flowLeak}>
                    <path className={styles.leakOut} d="M180 214 H448" markerEnd="url(#arrowLeak)" />
                    <text className={styles.stepLeak} x="314" y="206" textAnchor="middle">…the same lookup, leaked</text>
                    <path className={styles.leakBack} d="M448 234 H180" markerEnd="url(#arrowLeakBack)" />
                    <text className={styles.stepLeakMuted} x="314" y="250" textAnchor="middle">answered by a stranger</text>
                </g>

                <defs>
                    <marker id="arrowOk" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path className={styles.headOk} d="M0 0 L6 3 L0 6 Z" />
                    </marker>
                    <marker id="arrowOkBack" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path className={styles.headOk} d="M0 0 L6 3 L0 6 Z" />
                    </marker>
                    <marker id="arrowLeak" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path className={styles.headLeak} d="M0 0 L6 3 L0 6 Z" />
                    </marker>
                    <marker id="arrowLeakBack" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                        <path className={styles.headLeak} d="M0 0 L6 3 L0 6 Z" />
                    </marker>
                </defs>
            </svg>
            <figcaption className={styles.caption}>
                A DNS leak is a lookup going somewhere you did not choose.
            </figcaption>
        </figure>
    )
}
